/* eslint-disable camelcase */
import { Country } from "@utils/constants/localisation";
import { getWithIDToken, IMSTokenParams, postWithIdToken } from "@utils/http/MSAuthAPI";
import { CustomerDocumentUploadRequest, ICustomerDocumentUploadResponse, UploadParams, UploadResult } from './DocumentStore.types';

// We can use alternateHost to point to localhost for testing
//const alternateHost = 'http://localhost:5000';
const alternateHost = '';

interface IDocumentStoreService {
    getIdentityVerificationCheckDocuments(identityVerificationCheckReferenceId: string, IDTokenParams: IMSTokenParams): Promise<any>
    generatePresignedDownloadURL(documentStoreRecordId: string, s3Key: string, IDTokenParams: IMSTokenParams): Promise<any>
    prepareCustomerDocumentUpload(
        request: CustomerDocumentUploadRequest,
        IDTokenParams: IMSTokenParams,
        country: Country,
        zohoContactId: string,
    ): Promise<ICustomerDocumentUploadResponse>
    uploadFile(presignedUploadUrl: string, documentFile: File, fileType: string): Promise<UploadResult>
    uploadFiles(uploads: UploadParams[]): Promise<UploadResult[]>
}

const DocumentStoreService: IDocumentStoreService = {
    async getIdentityVerificationCheckDocuments(identityVerificationCheckReferenceId: string, IDTokenParams: IMSTokenParams, documentTypes?: string[]): Promise<any> {
        const params: { generate_presigned_download_url: string, document_types?: string[]} = { generate_presigned_download_url: 'true'};

        documentTypes ? params.document_types = documentTypes : undefined

        const resp = await getWithIDToken(
            `${alternateHost}/v1/document-store/identity-verification-check/${identityVerificationCheckReferenceId}`,
            IDTokenParams,
            params
        );

        if (resp.status >= 500) {
            throw new Error('Server error');
        }

        return resp.data;
    },

    async generatePresignedDownloadURL(documentStoreRecordId: string, s3Key: string, IDTokenParams: IMSTokenParams): Promise<any> {
        const params: { s3_key: string } = { s3_key: s3Key };

        const resp = await getWithIDToken(
            `${alternateHost}/v1/document-store/document/${documentStoreRecordId}`,
            IDTokenParams,
            params
        );

        if (resp.status >= 500) {
            throw new Error('Server error');
        }

        return resp.data;
    },

    async prepareCustomerDocumentUpload(
        request: CustomerDocumentUploadRequest,
        IDTokenParams: IMSTokenParams,
        country: Country,
        zohoContactId: string,
    ): Promise<ICustomerDocumentUploadResponse> {
        const resp = await postWithIdToken(
            `${alternateHost}/v1/document-store/customer/${country}/${zohoContactId}`,
            IDTokenParams,
            {},
            { ...request.toJSON() }
        );

        if (resp.status === 500) {
            throw new Error('Server error');
        }

        return resp.data;
    },

    async uploadFile(presignedUploadUrl: string, documentFile: File, fileType: string): Promise<UploadResult> {
        try {
            const response = await fetch(presignedUploadUrl, {
                method: 'PUT',
                headers: new Headers({ 'Content-Type': fileType }),
                body: documentFile
            });

            return {
                success: response.ok,
                response
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    },

    async uploadFiles(uploads: UploadParams[]): Promise<UploadResult[]> {
        const uploadPromises = uploads.map(({ presignedUrl, file, contentType }) =>
            DocumentStoreService.uploadFile(presignedUrl, file, contentType)
        );

        return Promise.all(uploadPromises);
    },
}

export default DocumentStoreService;